/**
 * avatars/ao-dai-flaps.js
 * Quản lý tà áo dài 3D và các chi tiết trang phục phụ trợ (Hoodie, phụ kiện thân)
 */

import * as THREE from 'three';
import { generateProceduralTexture } from './avatar-presets.js';

/**
 * Gắn tà áo dài vào thân nhân vật
 * @param {object} avatar
 * @param {string} shirtColor
 */
export function attachAoDaiFlaps(avatar, shirtColor = avatar.config.colors.shirt) {
  removeAoDaiFlaps(avatar);
  const { torsoW, legH, torsoD } = avatar.dimensions;
  const flapMat = new THREE.MeshStandardMaterial({ color: shirtColor, roughness: 0.6 });
  const flapLen = legH * 0.78;
  const flapGeo = new THREE.BoxGeometry(torsoW * 0.95, flapLen, 0.02);

  // Mặt ngoài tà trước vẽ tiếp thân sen & lá sen từ thân áo xuống
  const flapTex = new THREE.CanvasTexture(generateProceduralTexture('ao_dai_flap', { shirtColor }));
  flapTex.colorSpace = THREE.SRGBColorSpace;
  const flapFrontMat = new THREE.MeshStandardMaterial({ map: flapTex, roughness: 0.6 });
  avatar.disposables.push(flapTex, flapFrontMat);

  // Mỗi tà treo trên một bản lề ở eo; update() xoay bản lề theo chân bước nhanh nhất
  // về phía trước (tà trước) / phía sau (tà sau) để tà luôn nằm ngoài ống chân
  const makeFlap = (side) => {
    const hinge = new THREE.Group();
    hinge.position.set(0, 0, side * torsoD * 0.52);
    hinge.userData.flapSide = side;
    const flap = new THREE.Mesh(
      flapGeo,
      side === 1 ? [flapMat, flapMat, flapMat, flapMat, flapFrontMat, flapMat] : flapMat
    );
    flap.position.y = -flapLen / 2 + 0.04;
    flap.rotation.x = side * 0.05;
    flap.castShadow = false;
    flap.receiveShadow = true;
    hinge.add(flap);
    avatar.parts.torso.add(hinge);
    return hinge;
  };
  const frontFlap = makeFlap(1);
  const backFlap = makeFlap(-1);

  avatar.flaps.push(frontFlap, backFlap);
  avatar.disposables.push(flapMat, flapGeo);
  avatar.enforceShadowPolicy();
}

/**
 * Gắn chi tiết 3D cho trang phục.
 * @param {object} avatar
 * @param {string} outfitId
 */
export function attachOutfitExtras(avatar, outfitId) {
  removeAoDaiFlaps(avatar);
  if (outfitId === 'ao_dai') {
    attachAoDaiFlaps(avatar, avatar.config.colors.shirt);
    return;
  }
  const { torsoW, torsoH, torsoD } = avatar.dimensions;
  const add = (w, h, d, color, x, y, z, rotX = 0) => {
    const geo = new THREE.BoxGeometry(w, h, d);
    const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.7 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, y, z);
    mesh.rotation.x = rotX;
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    avatar.parts.torso.add(mesh);
    avatar.flaps.push(mesh);
    avatar.disposables.push(geo, mat);
    return mesh;
  };
  if (outfitId === 'genz_oversize') {
    // Hoodie: mũ trùm gấp sau gáy & dây rút trước ngực
    const col = avatar.config.colors.shirt || '#1e1b4b';
    add(torsoW * 0.72, 0.16, 0.1, col, 0, torsoH - 0.1, -(torsoD / 2 + 0.05), -0.25);
    [-1, 1].forEach((side) => add(0.014, 0.16, 0.012, '#e2e8f0', side * 0.05, torsoH - 0.12, torsoD / 2 + 0.008));
  }
  avatar.enforceShadowPolicy();
}

/**
 * Gỡ bỏ toàn bộ tà áo hoặc phụ kiện 3D trên thân
 * @param {object} avatar
 */
export function removeAoDaiFlaps(avatar) {
  if (avatar.flaps && avatar.flaps.length) {
    avatar.flaps.forEach((f) => {
      if (f.parent) f.parent.remove(f);
    });
    avatar.flaps = [];
  }
}

/**
 * Cập nhật góc bay của tà áo dài theo chuyển động chân
 * @param {object} avatar
 */
export function updateAoDaiFlaps(avatar) {
  if (!avatar.flaps || !avatar.flaps.length || avatar.config.features.outfit !== 'ao_dai') return;
  const { leftLeg, rightLeg, torso } = avatar.parts;
  if (!leftLeg || !rightLeg) return;
  const torsoRot = torso.rotation.x;
  const l = leftLeg.rotation.x - torsoRot;
  const r = rightLeg.rotation.x - torsoRot;
  avatar.flaps.forEach((hinge) => {
    const side = hinge.userData.flapSide;
    if (side === 1) hinge.rotation.x = Math.min(0, l, r);
    else if (side === -1) hinge.rotation.x = Math.max(0, l, r);
  });
}
